import React from 'react';

import toast from 'react-hot-toast';

type ActionToastOptions = {
  /**
   * Toast body. A plain string is fine for short messages; pass a node when a
   * segment needs to truncate (e.g. a long filename) so the action button is
   * never pushed off the 350px toast.
   */
  message: React.ReactNode;
  /** Button label, e.g. "Undo" or "Map fields →". */
  actionLabel: string;
  /** Runs when the user clicks the action (the toast dismisses itself first). */
  onAction: () => void;
  /** data-testid for the action button. */
  testId?: string;
};

/**
 * Success toast with one inline action. Two uses in the upload flow:
 * Undo for reversible bulk archive/restore (Gmail pattern), and the
 * "tape parsed — Map fields →" hand-off from upload to mapping.
 */
export function showActionToast({
  message,
  actionLabel,
  onAction,
  testId,
}: ActionToastOptions): void {
  toast(
    (t) => (
      <div className="flex items-center gap-3">
        {/* min-w-0 lets the message shrink so a long filename truncates instead
            of shoving the action button past the toast's max-width. */}
        <div className="min-w-0 flex-1">{message}</div>
        <button
          type="button"
          data-testid={testId}
          className="shrink-0 whitespace-nowrap rounded-md border border-slate-400 px-2 py-0.5 text-sm font-medium hover:bg-slate-100 hover:text-slate-900"
          onClick={() => {
            toast.dismiss(t.id);
            onAction();
          }}
        >
          {actionLabel}
        </button>
      </div>
    ),
    // Long enough to read + act; short enough not to linger (Gmail ~5-10s).
    // Slightly wider than the 350px default so a title + action button fit
    // without crowding; richer than a plain status toast, so the extra room reads.
    { duration: 8000, icon: '✓', style: { maxWidth: '420px' } },
  );
}

type UndoToastOptions = {
  message: string;
  onUndo: () => void;
};

/** Undo variant — reversible actions apply immediately and this is the escape hatch. */
export function showUndoToast({ message, onUndo }: UndoToastOptions): void {
  showActionToast({ message, actionLabel: 'Undo', onAction: onUndo, testId: 'undo-toast-button' });
}
